import { useContext, useEffect, useState, useRef, useCallback } from "react"
import { Link, useMatch } from "react-router-dom"
import { toast } from "react-toastify"
import Spinner from "../../../components/BootstrapCompo"
import { GlobalContext, handleError } from "../../../context/Provider"
import henceforthApi from "../../../utils/henceforthApi"

const CategoryEdit = () => {
    const match = useMatch("/category/level-1/edit/:name/:type/:id")
    const [categoryNameError, setCategoryError] = useState("")
    const [categoryName, setCategoryName] = useState(match?.params.name)
    const [loading, setloading] = useState(false)
    const [productType, setProductType] = useState<string>("")
    const [imageError, setImageError] = useState("")
    const [selectedFile, setSelectedFile] = useState<any>(null)
    const [existingImage, setExistingImage] = useState("")
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { authState } = useContext(GlobalContext)

    // Product types state
    const [productTypes, setProductTypes] = useState<any[]>([])
    const [ptTotalCount, setPtTotalCount] = useState(0)
    const [ptLoading, setPtLoading] = useState(false)
    const ptPageRef = useRef(1)
    const isFetchingRef = useRef(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownContainerRef = useRef<HTMLDivElement>(null)

    const fetchProductTypes = useCallback(async (pg: number, replace = false) => {
        if (isFetchingRef.current) return
        isFetchingRef.current = true
        setPtLoading(true)
        try {
            const res = await henceforthApi.ProductTypes.list(pg, 10, "", true)
            const apiData = res.data
            const newItems = Array.isArray(apiData.data) ? apiData.data : []
            setProductTypes(prev => replace ? newItems : [...prev, ...newItems])
            setPtTotalCount(apiData.total_count || 0)
            ptPageRef.current = pg
        } catch (error) {
            handleError(error)
        } finally {
            setPtLoading(false)
            isFetchingRef.current = false
        }
    }, [])

    useEffect(() => {
        fetchProductTypes(1, true)
    }, [])


    const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5
        if (isAtBottom && !ptLoading && productTypes.length < ptTotalCount) {
            fetchProductTypes(ptPageRef.current + 1)
        }
    }

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(e.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const fileUpload = async () => {
        try {
            const apiRes = await henceforthApi.Common.do_spaces_file_upload("file", selectedFile)
            return apiRes.data.file_name
        } catch (error) {
            handleError(error)
            return ""
        }
    }

    const initializeCategory = async () => {
        try {
            let apiRes = await henceforthApi.Category.listCategory(`_id=${match?.params.id}`)
            if (apiRes?.data && apiRes.data.length > 0) {
                const category = apiRes.data[0]
                setCategoryName(category.name)
                setProductType(category.product_type)   // ✅ pre-selects in dropdown
                setExistingImage(category.image || "")
            }
        } catch (error) {
            handleError(error)
        }
    }

    useEffect(() => {
        initializeCategory()
    }, [match?.params.id])

    const editCategory = async (e: any) => {
        e.preventDefault()
        if (!categoryName) return setCategoryError("Please Enter Category Name")
        setloading(true)
        try {
            let image = existingImage
            if (selectedFile) {
                const uploadedImage = await fileUpload()
                if (uploadedImage) image = uploadedImage
            }
            await henceforthApi.Category.editCategory({
                _id: match?.params.id,
                name: categoryName,
                product_type: productType,
                ...(image && { image }),
                language: authState.lang
            })
            toast.success("Category Updated Successfully")
            window.history.back()
        } catch (error) {
            handleError(error)
        } finally {
            setloading(false)
        }
    }

    return (
        <>
            <section className="breadcrum-box">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <h2 className="fw-semibold">Edit Categories</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item active fw-bold">Edit Categories</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <div className='page-spacing'>
                <section className='product-detail'>
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-sm-10 col-md-6 col-lg-5 col-xl-4 col-xxl-3">
                                <div className="common-card">
                                    <div className="common-card-title">
                                        <h5>Edit Categories</h5>
                                    </div>
                                    <div className="common-card-content">
                                        <form onSubmit={editCategory}>

                                            {/* Upload Image */}
                                            <div className='upload-fields-box mb-4'>
                                                <label className="mb-1 fw-bold">Category Image:</label>
                                                <div className='d-flex align-items-start gap-3'>
                                                    <div
                                                        style={{
                                                            width: '100px', height: '100px',
                                                            border: '2px dashed #ccc', borderRadius: '8px',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            backgroundColor: '#f8f9fa', flexShrink: 0, cursor: 'pointer'
                                                        }}
                                                        onClick={() => fileInputRef.current?.click()}
                                                    >
                                                        {selectedFile ? (
                                                            <img src={URL.createObjectURL(selectedFile)} alt="Category"
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                                        ) : existingImage ? (
                                                            <img src={`${henceforthApi.API_FILE_ROOT_ORIGINAL}${existingImage}`} alt="Category"
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                                        ) : (
                                                            <i className="fa fa-image" style={{ fontSize: '40px', color: '#6c757d' }} />
                                                        )}
                                                    </div>
                                                    <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/jpg"
                                                        onChange={(e: any) => { setSelectedFile(e.target.files[0]); setImageError("") }}
                                                        style={{ display: 'none' }} />
                                                </div>
                                                <p><small><strong>Note:-</strong> Please upload only .jpg and .png format only. (Optional)</small></p>
                                                {imageError && <div className="invalid-feedback d-block">{imageError}</div>}
                                            </div>

                                            {/* Category Name */}
                                            <div className="form-fields-box mb-3">
                                                <label className="mb-1 fw-semibold">Category Name:</label>
                                                <input type="text" placeholder="Category Name"
                                                    className={`form-control ${categoryNameError ? 'is-invalid' : ''} rounded-0`}
                                                    value={categoryName}
                                                    onChange={(e: any) => { if (categoryNameError) setCategoryError(""); setCategoryName(e.target.value) }} />
                                                {categoryNameError && <div className="invalid-feedback">{categoryNameError}</div>}
                                            </div>

                                            {/* Product Type Custom Dropdown */}
                                            <div className="mb-4" ref={dropdownContainerRef} style={{ position: 'relative' }}>
                                                <label className="mb-1 fw-semibold">Product Type:</label>

                                                {/* Trigger Button */}
                                                <button
                                                    type="button"
                                                    className="form-select text-start d-flex justify-content-between align-items-center"
                                                    style={{ cursor: 'pointer', color: productType ? '#212529' : '#6c757d' }}
                                                    onClick={() => setIsDropdownOpen(prev => !prev)}
                                                >
                                                    <span>
                                                        {productType
                                                            ? productTypes.find((pt: any) => pt._id === productType)?.name || productType
                                                            : "Product Type"}
                                                    </span>

                                                </button>

                                                {/* Dropdown List */}
                                                {isDropdownOpen && (
                                                    <div
                                                        onScroll={handleDropdownScroll}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            zIndex: 999,
                                                            background: '#fff',
                                                            border: '1px solid #ced4da',
                                                            borderRadius: '4px',
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                        }}
                                                    >
                                                        {productTypes.map((pt: any) => (
                                                            <div
                                                                key={pt._id}
                                                                onClick={() => {
                                                                    setProductType(pt._id)
                                                                    setIsDropdownOpen(false)
                                                                }}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    cursor: 'pointer',
                                                                    backgroundColor: productType === pt._id ? '#e9f0ff' : 'transparent',
                                                                    color: productType === pt._id ? '#0d6efd' : '#212529',
                                                                    fontWeight: productType === pt._id ? 600 : 400,
                                                                }}
                                                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = productType === pt._id ? '#e9f0ff' : '#f8f9fa')}
                                                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = productType === pt._id ? '#e9f0ff' : 'transparent')}
                                                            >
                                                                {pt.name}
                                                            </div>
                                                        ))}

                                                        {ptLoading && (
                                                            <div style={{ padding: '8px 12px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                                                                Loading more...
                                                            </div>
                                                        )}

                                                        {!ptLoading && productTypes.length === 0 && (
                                                            <div style={{ padding: '8px 12px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                                                                No product types found
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Submit Button */}
                                            <div className="button-box">
                                                <button type="submit" className='btn btn-theme w-100' disabled={loading}>
                                                    {loading ? <Spinner /> : "Edit Category"}
                                                </button>
                                            </div>

                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}
export default CategoryEdit